/**
 * Command Registry
 * Phase 6: Interaction Layer - Command System
 * 
 * Provides a centralized command execution system for the application.
 * Commands can be registered, unregistered, and executed with parameters.
 */

import { eventBus } from '@core/event-bus'

/**
 * Command handler function type
 */
export type CommandHandler = (params?: any) => Promise<any> | any

/**
 * Command parameter definition
 */
export interface CommandParameter {
  name: string
  type: string
  required: boolean
}

/**
 * Command information
 */
export interface CommandInfo {
  name: string
  handler: CommandHandler
  moduleName: string
  description?: string
  parameters?: CommandParameter[]
}

/**
 * Command execution result
 */
export interface CommandResult<T = any> {
  success: boolean
  data?: T
  error?: string
}

/**
 * Command Registry
 * Manages command registration and execution
 */
export class CommandRegistry {
  private commands: Map<string, CommandInfo> = new Map()

  /**
   * Register a command
   */
  registerCommand(name: string, handler: CommandHandler, moduleName: string, description?: string, parameters?: CommandParameter[]): void {
    if (this.commands.has(name)) {
      console.warn(`Command '${name}' already registered, overwriting`)
    }

    const commandInfo: CommandInfo = {
      name,
      handler,
      moduleName,
      description: description || '',
      parameters: parameters || []
    }

    this.commands.set(name, commandInfo)
    eventBus.emit('command:registered', { name, moduleName })
  }

  /**
   * Unregister a command
   */
  unregisterCommand(name: string): void {
    const command = this.commands.get(name)
    if (command) {
      this.commands.delete(name)
      eventBus.emit('command:unregistered', { name, moduleName: command.moduleName })
    }
  }

  /**
   * Get a command by name
   */
  getCommand(name: string): CommandInfo | undefined {
    return this.commands.get(name)
  }

  /**
   * Check if a command exists
   */
  hasCommand(name: string): boolean {
    return this.commands.has(name)
  }

  /**
   * Execute a command
   */
  async executeCommand<T = any>(name: string, params?: any): Promise<CommandResult<T>> {
    const command = this.commands.get(name)
    
    if (!command) {
      const error = `Command '${name}' not found`
      eventBus.emit('command:error', { name, error })
      return { success: false, error }
    }

    try {
      // Emit command execution start
      eventBus.emit('command:executing', { name, moduleName: command.moduleName, params })
      
      // Execute the command handler
      const result = await command.handler(params)
      
      // Emit command execution success
      eventBus.emit('command:executed', { name, moduleName: command.moduleName, result })
      
      return { success: true, data: result }
    } catch (error: any) {
      const errorMessage = error?.message || 'Unknown error'
      eventBus.emit('command:error', { name, moduleName: command.moduleName, error: errorMessage })
      return { success: false, error: errorMessage }
    }
  }

  /**
   * Get all registered commands
   */
  getAllCommands(): CommandInfo[] {
    return Array.from(this.commands.values())
  }

  /**
   * Get commands by module name
   */
  getCommandsByModule(moduleName: string): CommandInfo[] {
    return Array.from(this.commands.values())
      .filter(cmd => cmd.moduleName === moduleName)
  }

  /**
   * Get all command names
   */
  getCommandNames(): string[] {
    return Array.from(this.commands.keys())
  }

  /**
   * Clear all commands
   */
  clear(): void {
    this.commands.clear()
  }

  /**
   * Get command count
   */
  getCommandCount(): number {
    return this.commands.size
  }
}

/**
 * Global command registry instance
 */
export const commandRegistry = new CommandRegistry()
